import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink, X, Copy } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, mediaUrl } from "@/lib/site";
import { Fld, BiText, UploadBtn } from "./ui";

const EMPTY = { title: {}, excerpt: {}, content: {}, image: "", published: true };

const PostEditor = ({ item, onClose, onSaved }) => {
  const [f, setF] = useState({ ...EMPTY, ...item });
  const set = (k) => (v) => setF((o) => ({ ...o, [k]: v }));
  const save = async () => {
    if (!f.title?.sk && !f.title?.en) return toast.error("Zadajte nadpis");
    try {
      if (f.id) await api.put(`/admin/posts/${f.id}`, f); else await api.post("/admin/posts", f);
      toast.success("Uložené"); onSaved();
    } catch { toast.error("Uloženie zlyhalo"); }
  };
  return (
    <div className="space-y-5" data-testid="post-editor">
      <Fld label="Fotka">
        <div className="flex items-center gap-4">
          {f.image ? (
            <div className="relative w-40 aspect-[4/3]"><img src={mediaUrl(f.image)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => set("image")("")} className="absolute top-1 right-1 bg-midnight/70 text-ivory p-1"><X size={12} /></button></div>
          ) : <div className="w-40 aspect-[4/3] bg-sand/40" />}
          <UploadBtn testid="post-upload-image" label="Nahrať fotku" onUploaded={set("image")} />
        </div>
      </Fld>
      <BiText label="Nadpis" value={f.title} onChange={set("title")} testid="post-title" />
      <BiText label="Krátky úvod (perex)" area rows={3} value={f.excerpt} onChange={set("excerpt")} testid="post-excerpt" />
      <BiText label="Text článku" area rows={12} value={f.content} onChange={set("content")} testid="post-content" />
      <label className="flex items-center gap-3 text-sm"><Switch checked={f.published} onCheckedChange={set("published")} data-testid="post-published" /> Zverejnený na webe</label>
      <div className="flex justify-end gap-3 pt-4 border-t border-sand">
        <button onClick={onClose} className="btn-ghost-dark" data-testid="post-cancel">Zrušiť</button>
        <button onClick={save} className="btn-gold" data-testid="post-save">Uložiť</button>
      </div>
    </div>
  );
};

export const BlogAdmin = () => {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/posts").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (p) => { await api.put(`/admin/posts/${p.id}`, { ...p, published: !p.published }); load(); };
  const del = async (p) => { if (window.confirm(`Zmazať „${p.title?.sk || p.title?.en}“?`)) { await api.delete(`/admin/posts/${p.id}`); load(); } };
  const postUrl = (p) => `${window.location.origin}/blog/${p.slug}`;
  const copy = (p) => navigator.clipboard?.writeText(postUrl(p)).then(() => toast.success("Odkaz skopírovaný")).catch(() => toast.error("Kopírovanie zlyhalo"));
  return (
    <div data-testid="admin-blog">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-3xl text-midnight">Blog</h2>
        <button className="btn-gold" onClick={() => setEdit({})} data-testid="add-post-btn"><Plus size={14} /> Nový príspevok</button>
      </div>
      {!items.length && <p className="text-midnight/60">Zatiaľ žiadne príspevky.</p>}
      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} className="flex flex-wrap sm:flex-nowrap items-center gap-4 bg-white border border-sand/60 p-3" data-testid={`admin-post-${p.slug}`}>
            {p.image ? <img src={mediaUrl(p.image)} alt="" className="w-20 h-16 object-cover shrink-0" /> : <div className="w-20 h-16 bg-sand/40 shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg text-midnight truncate">{p.title?.sk || p.title?.en}</div>
              <div className="text-xs text-midnight/60">{new Date(p.created_at).toLocaleDateString("sk-SK")} · {p.published ? "Zverejnený" : "Skrytý"}</div>
              <a href={postUrl(p)} target="_blank" rel="noreferrer" className="text-xs text-gold hover:underline truncate block mt-1" data-testid={`post-link-${p.slug}`}>{postUrl(p)}</a>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button title={p.published ? "Skryť" : "Zverejniť"} onClick={() => toggle(p)} data-testid={`toggle-post-${p.slug}`} className={p.published ? "text-green-700" : "text-midnight/30"}>{p.published ? <Eye size={18} /> : <EyeOff size={18} />}</button>
              <button title="Kopírovať odkaz" onClick={() => copy(p)} data-testid={`copy-post-${p.slug}`} className="text-midnight/50 hover:text-gold"><Copy size={16} /></button>
              <a href={postUrl(p)} target="_blank" rel="noreferrer" title="Otvoriť na webe" data-testid={`open-post-${p.slug}`} className="text-midnight/50 hover:text-gold"><ExternalLink size={16} /></a>
              <button onClick={() => setEdit(p)} data-testid={`edit-post-${p.slug}`} className="text-midnight/70 hover:text-gold"><Pencil size={16} /></button>
              <button onClick={() => del(p)} data-testid={`delete-post-${p.slug}`} className="text-midnight/50 hover:text-red-600"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto bg-ivory">
          <DialogTitle className="font-display text-2xl text-midnight">{edit?.id ? "Upraviť príspevok" : "Nový príspevok"}</DialogTitle>
          {edit && <PostEditor item={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
